import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Clapperboard, ChevronsLeft, FileText, Folder, Image as ImageIcon, Languages, LayoutDashboard,
  LogOut, Menu, Moon, Newspaper, Package, ScanLine, Search, Settings, Signpost, Sun, X,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useI18n } from '@/i18n';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { Tooltip } from './ui/primitives';
import { CommandPalette } from './CommandPalette';

/**
 * Sidebar 240px (collapsible to 64px), content max 1440px (§10.1).
 *
 * Grouped, not one flat list of nine: a labelled group ("Kontent", "Boshqaruv",
 * "Tizim") tells a new marketer where a thing lives before they've learned the
 * icons, and it gives the eye rest stops on a rail it scans dozens of times a
 * day. Within a group, order is frequency of use — articles first, because it
 * is 90% of the work (§10.3). Collapsed, the labels fall away to a hairline so
 * the grouping survives without the words.
 */
// Every item carries the PAGE permission that gates it (see lib/permissions).
// An item with no `page` (the overview) is always visible; any other item is
// shown only when the user's role grants that page. A group with no visible
// items falls away entirely — header and all — so each role sees a rail that is
// exactly what it can reach, nothing more.
const NAV_GROUPS = [
  { items: [{ to: '/', key: 'overview', icon: LayoutDashboard, end: true }] },
  {
    label: 'groupApp',
    items: [{ to: '/stories', key: 'stories', icon: Clapperboard, page: 'stories' }],
  },
  {
    label: 'groupContent',
    items: [
      { to: '/pages', key: 'pages', icon: FileText, page: 'pages' },
      { to: '/articles', key: 'articles', icon: Newspaper, page: 'articles' },
      { to: '/services', key: 'services', icon: Package, page: 'services' },
      { to: '/media', key: 'media', icon: ImageIcon, page: 'media' },
    ],
  },
  {
    label: 'groupManage',
    items: [
      { to: '/categories', key: 'categories', icon: Folder, page: 'categories' },
      { to: '/redirects', key: 'redirects', icon: Signpost, page: 'redirects' },
      // Murojaatlar (leads) hozircha yashirilgan — foydalanuvchi so'roviga ko'ra.
      // { to: '/leads', key: 'leads', icon: MessageSquare, page: 'leads' },
    ],
  },
  {
    label: 'groupSystem',
    items: [
      // Operational oversight of the RS scanner app, not website content.
      { to: '/scans', key: 'scans', icon: ScanLine, page: 'scans' },
      { to: '/settings', key: 'settings', icon: Settings, page: 'users' },
    ],
  },
] as const;

export function AppShell() {
  const { t } = useI18n();
  const { user, can } = useAuth();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('ipost.sidebar') === 'collapsed');
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Below `lg` the rail is an off-canvas drawer, not a fixed column — a phone
  // has no room for a permanent 240px sidebar. Desktop is unchanged.
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem('ipost.sidebar', collapsed ? 'collapsed' : 'open');
  }, [collapsed]);

  // A route change means the drawer did its job — close it so the page shows.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // ⌘K / Ctrl+K opens the palette; Escape closes the mobile drawer. Power users
  // stop touching the sidebar within a week (§10.6).
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
      if (event.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="flex min-h-screen bg-canvas">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-control focus:bg-brand-500 focus:px-4 focus:py-2 focus:text-white"
      >
        Asosiy qismga o‘tish
      </a>

      {/* Drawer scrim — mobile only, taps to dismiss. */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-40 bg-ink-900/50 backdrop-blur-[2px] lg:hidden"
          aria-hidden
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex h-screen w-sidebar shrink-0 flex-col border-r border-line bg-surface',
          // Mobile: slide in/out as a drawer. Desktop: sticky column that resizes.
          'transition-transform duration-base ease-out lg:sticky lg:top-0 lg:translate-x-0 lg:transition-[width]',
          collapsed ? 'lg:w-sidebar-collapsed' : 'lg:w-sidebar',
          mobileOpen ? 'translate-x-0 shadow-pop' : '-translate-x-full lg:shadow-none',
        )}
      >
        {/* Close affordance inside the drawer (mobile only). */}
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          aria-label={t.common.close}
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-control text-muted transition-ui duration-instant hover:bg-raised hover:text-strong lg:hidden"
        >
          <X aria-hidden className="h-4 w-4" />
        </button>

        <div className={cn('flex h-16 items-center gap-2.5 border-b border-line/70 px-4', collapsed && 'lg:justify-center lg:px-0')}>
          <Logo />
          {(
            <span className={cn('flex min-w-0 flex-col leading-none', collapsed && 'lg:hidden')}>
              <span className="font-display text-[1.0625rem] font-extrabold tracking-tight text-strong">
                {t.brand.name}
              </span>
              <span className="mt-1 truncate text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-faint">
                {t.brand.suffix}
              </span>
            </span>
          )}
        </div>

        <nav className="flex flex-1 flex-col gap-5 overflow-y-auto px-3 py-4">
          {NAV_GROUPS.map((group) => {
            // Keep only items the role may reach; a page-less item (overview) is
            // always kept. A group left empty by that filter is dropped entirely.
            const items = group.items.filter(
              (item) => !('page' in item) || can(item.page as Parameters<typeof can>[0]),
            );
            if (items.length === 0) return null;
            return (
              <div key={items[0].to} className="flex flex-col gap-0.5">
                {'label' in group && group.label && (
                  <>
                    <p
                      className={cn(
                        'mb-1 px-3 text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-faint/90',
                        collapsed && 'lg:hidden',
                      )}
                    >
                      {t.nav[group.label]}
                    </p>
                    {collapsed && (
                      <span aria-hidden className="mx-auto mb-1 hidden h-px w-6 rounded-chip bg-line lg:block" />
                    )}
                  </>
                )}
                {items.map((item) => (
                  <SidebarLink
                    key={item.to}
                    to={item.to}
                    end={'end' in item ? item.end : false}
                    icon={<item.icon aria-hidden strokeWidth={1.85} className="h-[1.125rem] w-[1.125rem] shrink-0" />}
                    label={t.nav[item.key]}
                    collapsed={collapsed}
                  />
                ))}
              </div>
            );
          })}
        </nav>

        <Tooltip side="right" label={collapsed ? t.common.expandSidebar : t.common.collapseSidebar}>
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? t.common.expandSidebar : t.common.collapseSidebar}
            className={cn(
              // Collapsing is a desktop affordance — the drawer is always full width.
              'm-3 hidden cursor-pointer items-center gap-2 rounded-control border border-line/70 px-3 py-2 text-ui font-medium text-muted lg:flex',
              'transition-ui duration-instant hover:border-line-strong hover:bg-raised hover:text-strong',
              collapsed && 'justify-center px-0',
            )}
          >
            <ChevronsLeft aria-hidden strokeWidth={1.85} className={cn('h-4 w-4 shrink-0 transition-transform duration-base', collapsed && 'rotate-180')} />
            {!collapsed && <span>{t.common.collapseSidebar}</span>}
          </button>
        </Tooltip>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-2 border-b border-line bg-surface/90 px-4 backdrop-blur sm:gap-3 sm:px-6">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label={t.common.openMenu}
            className="-ml-1 flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-control text-muted transition-ui duration-instant hover:bg-raised hover:text-strong lg:hidden"
          >
            <Menu aria-hidden className="h-5 w-5" strokeWidth={1.85} />
          </button>
          <Breadcrumb pathname={location.pathname} />
          <div className="flex-1" />

          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="flex cursor-pointer items-center gap-2 rounded-control border border-line px-3 py-1.5 text-ui text-muted transition-ui duration-instant hover:border-line-strong hover:text-strong dark:hover:border-brand-500/60"
          >
            <Search aria-hidden className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{t.common.search}</span>
            <kbd className="hidden rounded border border-line bg-raised px-1.5 font-mono text-[0.6875rem] text-faint md:inline">
              ⌘K
            </kbd>
          </button>

          <ThemeToggle />
          <LangToggle />

          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                className="flex h-9 cursor-pointer items-center gap-2 rounded-control pl-1 pr-2 transition-ui duration-instant hover:bg-raised"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-chip bg-brand-500 font-display text-[0.75rem] font-bold text-white">
                  {initials(user?.name)}
                </span>
                {/* First name only. The surname adds width to a bar that is
                    already crowded and tells the person nothing they don't
                    know — the full name is in the menu below. */}
                <span className="hidden text-ui text-strong lg:inline">{firstName(user?.name)}</span>
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={6}
                className="z-50 min-w-[13rem] rounded-card border border-line bg-surface p-1 shadow-pop data-[state=open]:animate-[fade-rise_160ms_var(--ease-out)]"
              >
                <div className="px-3 py-2">
                  <p className="truncate text-ui font-medium text-strong">{user?.name}</p>
                  <p className="truncate font-mono text-[0.6875rem] text-faint">
                    {user?.username ?? user?.email}
                  </p>
                  <p className="mt-1 text-[0.6875rem] text-muted">
                    {user?.role?.name ?? '—'}
                  </p>
                </div>
                <DropdownMenu.Separator className="my-1 h-px bg-line" />
                <SignOutItem />
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </header>

        {/* Route change: 140ms opacity + 4px rise. No slide — sliding pages
            fake depth that is not there (§10.7). */}
        <main id="main" key={location.pathname} className="anim-enter min-w-0 flex-1">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}

function SidebarLink({
  to, icon, label, collapsed, end, badge,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  collapsed: boolean;
  end?: boolean;
  badge?: number;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-2.5 rounded-panel py-1.5 pl-1.5 pr-3 text-ui',
          'transition-ui duration-instant ease-out active:scale-[0.99]',
          collapsed && 'lg:justify-center lg:px-0',
          // Three coordinated signals, all sharing the brand so the eye reads
          // one state, not three decorations: a filled icon tile anchors the
          // row, a hairline-ringed tint fills it, and the label goes semibold.
          // The filled tile is the anchor a marker used to be — it survives the
          // collapsed rail, where a 3px edge line would be lost.
          isActive
            ? 'bg-brand-500/[0.08] font-semibold text-brand-700 ring-1 ring-inset ring-brand-500/12 dark:bg-brand-500/[0.14] dark:text-brand-100 dark:ring-brand-400/20'
            : 'font-medium text-muted nav-hover',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Icon tile: filled brand when active (white glyph), quiet otherwise.
              Fixed 28px square so labels align whether or not the row is lit. */}
          <span
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-control transition-ui duration-instant',
              isActive
                ? 'bg-brand-500 text-white shadow-[inset_0_1px_0_rgb(255_255_255_/_0.22)] dark:bg-brand-500'
                : 'text-faint group-hover:text-strong',
            )}
          >
            {icon}
          </span>
          <span className={cn('truncate', collapsed && 'lg:hidden')}>{label}</span>
          {badge !== undefined && badge > 0 && (
            <span
              className={cn(
                'ml-auto rounded-chip bg-accent-500 px-1.5 font-mono text-[0.6875rem] leading-5 text-white shadow-[inset_0_1px_0_rgb(255_255_255_/_0.22)]',
                collapsed && 'lg:hidden',
              )}
            >
              {badge}
            </span>
          )}
          {collapsed && badge !== undefined && badge > 0 && (
            <span aria-hidden className="absolute right-1.5 top-1.5 hidden h-2 w-2 rounded-chip bg-accent-500 ring-2 ring-surface lg:block" />
          )}
        </>
      )}
    </NavLink>
  );
}

function Logo() {
  return (
    <span
      className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-control shadow-[inset_0_0_0_1px_rgb(255_255_255_/_0.10)]"
      aria-hidden
    >
      <img src="/logos/icon-square.png" alt="" className="h-full w-full object-cover" />
    </span>
  );
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const { t } = useI18n();
  const label = theme === 'dark' ? t.theme.switchToLight : t.theme.switchToDark;
  return (
    <Tooltip side="bottom" label={label}>
      <button
        type="button"
        onClick={toggle}
        aria-label={label}
        className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-control text-muted transition-ui duration-instant hover:bg-raised hover:text-strong"
      >
        {theme === 'dark' ? <Sun aria-hidden className="h-4 w-4" strokeWidth={1.85} /> : <Moon aria-hidden className="h-4 w-4" strokeWidth={1.85} />}
      </button>
    </Tooltip>
  );
}

/**
 * The two-letter code is the switch, but two letters alone do not say what
 * happens when you press them — the tooltip names the destination language.
 */
function LangToggle() {
  const { lang, setLang, t } = useI18n();
  const next = lang === 'uz' ? 'ru' : 'uz';
  const label = `${t.palette.switchLang}: ${next === 'ru' ? 'Русский' : "O'zbekcha"}`;
  return (
    <Tooltip side="bottom" label={label}>
      <button
        type="button"
        onClick={() => setLang(next)}
        aria-label={label}
        className="flex h-9 cursor-pointer items-center justify-center gap-1 rounded-control px-2.5 text-muted transition-ui duration-instant hover:bg-raised hover:text-strong"
      >
        <Languages aria-hidden className="h-4 w-4" strokeWidth={1.85} />
        <span className="font-mono text-code font-medium uppercase">{lang}</span>
      </button>
    </Tooltip>
  );
}

function SignOutItem() {
  const { signOut } = useAuth();
  const { t } = useI18n();
  return (
    <DropdownMenu.Item
      onSelect={() => void signOut()}
      className="flex cursor-pointer items-center gap-2 rounded-control px-3 py-2 text-ui text-muted outline-none transition-ui duration-instant data-[highlighted]:bg-raised data-[highlighted]:text-strong"
    >
      <LogOut aria-hidden className="h-4 w-4" />
      {t.auth.signOut}
    </DropdownMenu.Item>
  );
}

function Breadcrumb({ pathname }: { pathname: string }) {
  const { t } = useI18n();
  // A bare record id ("… / 2") tells nobody anything, and the editor header
  // already shows the title. Drop the last segment when it is an id.
  const segments = pathname
    .split('/')
    .filter(Boolean)
    .filter((segment, index, all) => index < all.length - 1 || !isIdSegment(segment));
  const map: Record<string, string> = {
    articles: t.nav.articles,
    services: t.nav.services,
    stories: t.nav.stories,
    pages: t.nav.pages,
    media: t.nav.media,
    categories: t.nav.categories,
    redirects: t.nav.redirects,
    leads: t.nav.leads,
    settings: t.nav.settings,
  };

  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 overflow-hidden text-ui">
      <NavLink
        to="/"
        className="shrink-0 cursor-pointer whitespace-nowrap text-muted transition-ui duration-instant hover:text-strong"
      >
        {t.nav.overview}
      </NavLink>
      {segments.map((segment, index) => {
        const last = index === segments.length - 1;
        return (
          <span key={segment + index} className={cn('flex items-center gap-1.5', last ? 'min-w-0' : 'shrink-0')}>
            <span aria-hidden className="text-faint">
              /
            </span>
            <span className={cn('whitespace-nowrap', last ? 'truncate text-strong' : 'text-muted')}>
              {map[segment] ?? segment}
            </span>
          </span>
        );
      })}
    </nav>
  );
}

/**
 * Numeric ids, cuids, prefixed ids ("s_air"), "new", and encoded routes like
 * %2Fservices. A bare record id in a breadcrumb tells nobody anything, and the
 * editor header already shows the title.
 */
function isIdSegment(segment: string): boolean {
  const decoded = decodeURIComponent(segment);
  if (decoded === 'new') return true;
  if (decoded.startsWith('/')) return true;
  if (/^\d+$/.test(decoded)) return true;
  if (/^[a-z]+_/i.test(decoded)) return true; // mock-style ids: s_air, u_admin
  return /^c[a-z0-9]{20,}$/i.test(decoded); // cuid
}

function firstName(name: string | undefined): string {
  return name?.trim().split(/\s+/)[0] ?? '';
}

function initials(name: string | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}
